import { ApolloClient, InMemoryCache, from, split } from '@apollo/client'
import { ON_SERVER } from 'shared/enums/environment'
import criticalEnv from 'helpers/criticalEnv'
import { WebSocketLink } from '@apollo/client/link/ws'
import { getMainDefinition } from '@apollo/client/utilities'
import { determineNodeEnvironment } from 'shared/helpers/determineEnvironment'
import { errorLink, httpLink, defaultOptions } from 'shared/helpers/apollo'

// Get the correct URI depending on SSR / frontend, and Docker / production.
export const getApolloURI = (isWebsocket = false) => {
  // This ENV var should always be set, it's the publicly accessible Hasura URL
  let publicInternetURI = criticalEnv('NEXT_PUBLIC_HASURA_GRAPHQL_ENDPOINT')

  // This ENV var only needs to be set locally for docker compose usage
  let dockerInternalURI =
    process.env.HASURA_GRAPHQL_DOCKER_ENDPOINT || publicInternetURI

  if (isWebsocket) {
    dockerInternalURI = 'wss://' + dockerInternalURI.split('//')[1]
    publicInternetURI = 'wss://' + dockerInternalURI.split('//')[1]
  }

  // Server side in docker compose we need to use the internal docker network
  return ON_SERVER ? dockerInternalURI : publicInternetURI
}

/**
 * The ON_SERVER check is to stop wsLink erroring server side / during builds (we only care about web sockets client side) and get around the 'Unable to find native implementation' error
 * It's odd because - we have set it to fallback to httpLink but we don't actually care about it falling back to anything. We just want it not to error.
 * After looking for something better it appears people have settled on an equivelant workaround anyway - https://github.com/apollographql/subscriptions-transport-ws/issues/333#issuecomment-359261024
 * and NextJS are speaking of no official solution - https://github.com/vercel/next.js/discussions/14950
 */
const wsLink =
  !ON_SERVER && !determineNodeEnvironment().isTest
    ? new WebSocketLink({
        uri: getApolloURI(true),
        options: {
          reconnect: true
        }
      })
    : httpLink()

/*
  Split Links will alter the direction in the chain based on whether a query or subscription
  Read https://www.apollographql.com/docs/react/data/subscriptions/ for an explanation
*/
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query)

    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription' &&
      !ON_SERVER
    )
  },
  wsLink,
  httpLink()
)

export const client = new ApolloClient({
  link: from([errorLink, splitLink]),
  cache: new InMemoryCache(),
  ssrMode: ON_SERVER,
  credentials: 'include',
  defaultOptions
})
